import React from 'react';
import { AutoSizer, List } from 'react-virtualized';
import { removeItem } from 'Store/helpers/array';
import ReportcenterFilterRow from './ReportcenterFilterRow';
import T from 'Components/Helpers/translate';
import type { RCDashboardFilter } from 'Resources/ReportDashboard';
import { type MinimalProduct } from 'App/resources/Model/Product';
import type ProductCategory from 'App/resources/Model/ProductCategory';

interface ReportcenterFilterProductAndCategoryProps {
	values: number[];
	onChange: (type: keyof RCDashboardFilter, values: number[], comparison: string) => void;
	content: MinimalProduct[] | ProductCategory[];
	resetFilter: () => void;
	filterType: 'ProductCategory' | 'Product';
	comparison: string;
}

// ReportcenterFilterRow is a fixed-height row: 1px border-top + 8px top padding (ptm)
// + 20px checkbox (sm) + 8px bottom padding (pbm) = 37px. The title is nowrap+ellipsis
// so it never grows the row. Keep ROW_HEIGHT in sync if the row's padding or checkbox
// size changes, otherwise the virtualized rows overlap or leave gaps.
//
// The dropdown is capped at 410px (ReportcenterFilter.scss). Above the list sit the
// ~92px search header and the 37px "no filter" row; below it the ~27px active/inactive
// toggle and the ~43px override-info footer. MAX_LIST_HEIGHT keeps the list short enough
// that the whole dropdown fits without an outer scrollbar. The list is virtualized so
// only the visible rows mount — accounts can have thousands of products and rendering
// them all froze the next interaction with an ~800ms layout pass.
const ROW_HEIGHT = 37;
const MAX_LIST_HEIGHT = 190;

const ReportcenterFilterProductAndCategory = ({
	onChange,
	content,
	filterType,
	resetFilter,
	values,
	comparison
}: ReportcenterFilterProductAndCategoryProps) => {
	const listHeight = Math.min(content.length * ROW_HEIGHT, MAX_LIST_HEIGHT);

	return (
		<>
			<ReportcenterFilterRow
				key={-1}
				onClick={() => {
					resetFilter();
				}}
				selected={!values.length}
				title={T('filters.noFilter')}
			/>
			<AutoSizer disableHeight>
				{({ width }) => (
					<List
						width={width}
						height={listHeight}
						rowCount={content.length}
						rowHeight={ROW_HEIGHT}
						overscanRowCount={10}
						// rowRenderer closes over `values`, so toggling a selection re-renders the
						// visible rows with the updated checkbox state.
						rowRenderer={({ key, index, style }) => {
							const row = content[index];
							return (
								<div key={key} style={style}>
									<ReportcenterFilterRow
										key={row.id}
										onClick={() => {
											const i = values.indexOf(row.id);
											onChange(
												filterType,
												i !== -1 ? removeItem(values, i) : [...values, row.id],
												comparison
											);
										}}
										selected={values.indexOf(row.id) !== -1}
										title={row.name}
									/>
								</div>
							);
						}}
					/>
				)}
			</AutoSizer>
		</>
	);
};

export default ReportcenterFilterProductAndCategory;
